import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

export const MagicWandIcon = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M15.232 5.232l3.536 3.536M9 11l-6 8 2 2 8-6m1.5-7.5l1.5-1.5a2.121 2.121 0 013 3L11 15l-4 1 1-4 7.5-7.5z"
    />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v2M3 5h2M19 11v2M17 13h2M7 17v1M6.5 17.5h1" />
  </svg>
);

const PICK_DURATION_MS = 4500;

const LOADING_JOKES = [
  'Why did the developer go broke? Because he used up all his cache.',
  'I told my computer I needed a break. It said: “No problem — I\'ll go to sleep.”',
  'My to-do list and I are in a toxic relationship. It keeps growing and I keep ignoring it.',
  'Productivity tip: if you can\'t decide what to do, let a magic wand decide. That\'s delegation.',
  'Why do programmers prefer dark mode? Because light attracts bugs.',
  'I had a joke about time management, but I\'ll tell you later.',
  'Parallel processing: doing many things at once and finishing none of them.',
  'My workflow is simple: pick a task, panic, then pick another task.',
];

const pickRandom = (items, excludeIds = new Set()) => {
  const available = (Array.isArray(items) ? items : []).filter((item) => {
    const id = item?.id;
    return id != null && !excludeIds.has(String(id));
  });
  if (!available.length) return null;
  return available[Math.floor(Math.random() * available.length)];
};

const pickRandomJoke = () => LOADING_JOKES[Math.floor(Math.random() * LOADING_JOKES.length)];

/**
 * Random task/workflow picker.
 * Initial open: brief loading with a joke, then reveal one random pick.
 * Next: instant re-pick (no repeat until pool exhausted).
 */
const MagicPickModal = ({
  isOpen,
  onClose,
  items = [],
  onOpenItem,
  title = 'Pick a task for me?',
}) => {
  const [phase, setPhase] = useState('idle'); // idle | loading | result | empty
  const [loadingJoke, setLoadingJoke] = useState('');
  const [current, setCurrent] = useState(null);
  const [shownIds, setShownIds] = useState([]);
  const pendingPickRef = useRef(null);

  const pool = useMemo(
    () => (Array.isArray(items) ? items.filter((item) => item?.id) : []),
    [items],
  );

  const drawNext = useCallback((exclude = []) => {
    const excludeSet = new Set(exclude.map(String));
    let next = pickRandom(pool, excludeSet);
    let nextShown = exclude;
    if (!next && pool.length) {
      next = pickRandom(pool, new Set());
      nextShown = [];
    }
    if (!next) {
      setCurrent(null);
      setShownIds([]);
      setPhase('empty');
      return;
    }
    setCurrent(next);
    setShownIds([...nextShown, String(next.id)]);
    setPhase('result');
  }, [pool]);

  useEffect(() => {
    if (!isOpen) {
      setPhase('idle');
      setLoadingJoke('');
      setCurrent(null);
      setShownIds([]);
      pendingPickRef.current = null;
      return undefined;
    }

    if (!pool.length) {
      setPhase('empty');
      setCurrent(null);
      return undefined;
    }

    const choice = pickRandom(pool);
    pendingPickRef.current = choice;
    setLoadingJoke(pickRandomJoke());
    setPhase('loading');
    setCurrent(null);
    setShownIds([]);

    const timerId = window.setTimeout(() => {
      const picked = pendingPickRef.current;
      if (picked) {
        setCurrent(picked);
        setShownIds([String(picked.id)]);
        setPhase('result');
      } else {
        setPhase('empty');
      }
    }, PICK_DURATION_MS);

    return () => window.clearTimeout(timerId);
  }, [isOpen, pool]);

  if (!isOpen) return null;

  const canNext = pool.length > 1 && phase === 'result';
  const remainingAfterCurrent = Math.max(0, pool.length - shownIds.length);
  const kindLabel = current?.kind === 'workflow' ? 'Workflow' : 'Task';

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[1000] overflow-y-auto">
      <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={onClose} aria-hidden="true" />
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="magic-pick-title"
          className="relative w-full max-w-md rounded-2xl bg-white dark:bg-gray-800 shadow-xl border border-violet-200/70 dark:border-violet-900/40 overflow-hidden"
        >
          <div className="flex items-start justify-between gap-3 px-5 pt-5 pb-3">
            <div className="flex items-start gap-3 min-w-0">
              <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-violet-100 dark:bg-violet-950/50 text-violet-600 dark:text-violet-300">
                <MagicWandIcon className="w-5 h-5" />
              </span>
              <div className="min-w-0">
                <h3 id="magic-pick-title" className="text-lg font-semibold text-gray-900 dark:text-white">
                  {title}
                </h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 leading-snug">
                  I know sometimes we get confused selecting something best because of options.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="flex-shrink-0 rounded-lg p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
              aria-label="Close"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="px-5 pb-5">
            {phase === 'loading' && (
              <div className="rounded-xl border border-violet-200/80 dark:border-violet-800/60 bg-gradient-to-br from-violet-50/80 via-white to-indigo-50/60 dark:from-violet-950/30 dark:via-gray-800/40 dark:to-indigo-950/20 px-5 py-7 text-center">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <span className="flex gap-1" aria-hidden="true">
                    {[0, 1, 2].map((i) => (
                      <span
                        key={i}
                        className="h-1.5 w-1.5 rounded-full bg-violet-500 dark:bg-violet-400 animate-bounce"
                        style={{ animationDelay: `${i * 0.15}s` }}
                      />
                    ))}
                  </span>
                </div>
                <p className="text-base font-bold tracking-wide bg-gradient-to-r from-violet-600 via-fuchsia-500 to-indigo-600 dark:from-violet-300 dark:via-fuchsia-300 dark:to-indigo-300 bg-clip-text text-transparent animate-pulse">
                  Loading
                  <span className="inline-flex w-[1.1em] text-left">
                    <span className="animate-pulse">.</span>
                    <span className="animate-pulse" style={{ animationDelay: '0.2s' }}>.</span>
                    <span className="animate-pulse" style={{ animationDelay: '0.4s' }}>.</span>
                  </span>
                </p>
                <p className="mt-5 text-[11px] font-semibold uppercase tracking-[0.18em] text-violet-500/80 dark:text-violet-400/90">
                  Here&apos;s a joke for you
                </p>
                <p className="mt-3 text-[15px] leading-relaxed italic font-medium bg-gradient-to-r from-violet-700 via-purple-600 to-indigo-700 dark:from-violet-200 dark:via-fuchsia-200 dark:to-indigo-200 bg-clip-text text-transparent">
                  &ldquo;{loadingJoke}&rdquo;
                </p>
              </div>
            )}

            {phase === 'empty' && (
              <div className="rounded-xl border border-dashed border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-900/40 px-4 py-8 text-center">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  No pending tasks or workflows to pick from right now.
                </p>
              </div>
            )}

            {phase === 'result' && current && (
              <>
                <p className="text-xs font-semibold uppercase tracking-wide text-violet-600 dark:text-violet-300 mb-2">
                  Here is your task
                </p>
                <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-violet-50/60 dark:bg-violet-950/20 px-4 py-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="inline-flex items-center rounded-md border border-violet-200 dark:border-violet-800 bg-white/80 dark:bg-gray-800/80 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-violet-700 dark:text-violet-300">
                      {kindLabel}
                    </span>
                    {current.meta ? (
                      <span className="text-[11px] font-medium text-gray-500 dark:text-gray-400 truncate">
                        {current.meta}
                      </span>
                    ) : null}
                  </div>
                  <h4 className="text-base font-semibold text-gray-900 dark:text-white leading-snug break-words">
                    {current.title || 'Untitled'}
                  </h4>
                  {current.subtitle ? (
                    <p className="mt-2 text-sm text-gray-600 dark:text-gray-300 leading-relaxed whitespace-pre-wrap break-words line-clamp-6">
                      {current.subtitle}
                    </p>
                  ) : null}
                </div>
              </>
            )}

            {(phase === 'result' || phase === 'empty') && (
              <div className="mt-5 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                >
                  Close
                </button>
                {phase === 'result' && (
                  <>
                    <button
                      type="button"
                      disabled={!canNext}
                      onClick={() => drawNext(shownIds)}
                      className="px-4 py-2 text-sm font-semibold text-violet-700 dark:text-violet-200 bg-violet-100 dark:bg-violet-950/50 border border-violet-200 dark:border-violet-800 rounded-lg hover:bg-violet-200/80 dark:hover:bg-violet-900/40 transition-colors disabled:opacity-40 disabled:pointer-events-none"
                      title={remainingAfterCurrent > 0 ? `${remainingAfterCurrent} left` : 'Reshuffle'}
                    >
                      Next
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        onOpenItem?.(current);
                        onClose?.();
                      }}
                      className="px-4 py-2 text-sm font-semibold text-white bg-violet-600 hover:bg-violet-700 rounded-lg transition-colors"
                    >
                      Open
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default MagicPickModal;
